import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import NotificationBell from './NotificationBell';
import './TopNavbar.css';

// 统一菜单项（15项）
const MAIN_MENU_ITEMS = [
  { key: 'home', icon: '🏠', label: '首页', path: '/' },
  { key: 'notes', icon: '📝', label: '笔记', path: '/notes' },
  { key: 'workorder', icon: '📊', label: '工单看板', path: '/workorder-dashboard' },
  { key: 'workorder-import', icon: '📝', label: '工单录入', path: '/workorder-import' },
  { key: 'handover', icon: '🔄', label: '运维交接', path: '/handover' },
  { key: 'manuals', icon: '📖', label: '操作手册管理', path: '/manuals' },
  { key: 'projects', icon: '📋', label: '项目管理', path: '/projects' },
  { key: 'monitor', icon: '🖥️', label: '运维监控', path: '/monitor' },
  { key: 'knowledge', icon: '📚', label: '知识库管理', path: '/knowledge' },
  { key: 'dictionary', icon: '🗃️', label: '数据字典', path: '/knowledge/dictionary' },
  { key: 'diagrams', icon: '📊', label: '流程图', path: '/diagrams' },
  { key: 'professional', icon: '🧑‍💼', label: '专业助手', path: '/professional-assistant' },
  { key: 'work-logs', icon: '📅', label: '工作日志', path: '/work-logs' },
];

const ADMIN_MENU_ITEMS = [
  { key: 'announcements', icon: '📢', label: '公告管理', path: '/announcements', adminOnly: true },
  { key: 'users', icon: '👥', label: '用户管理', path: '/users', adminOnly: true },
  { key: 'permissions', icon: '🔐', label: '权限管理', path: '/permissions', adminOnly: true },
];

const TopNavbar = ({ onLogout }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [username, setUsername] = useState('');
  const [visibleKeys, setVisibleKeys] = useState([]);
  const [isAdmin, setIsAdmin] = useState(false);

  useEffect(() => {
    const user = localStorage.getItem('notes_realname') || localStorage.getItem('notes_user') || '用户';
    setUsername(user);
    fetchMenuPermissions();
  }, [location.pathname]);

  const fetchMenuPermissions = () => {
    const token = localStorage.getItem('notes_token');
    if (!token) return;
    fetch(`${process.env.REACT_APP_API_URL || ''}/api/user/menu`, {
      headers: { 'x-token': token }
    })
      .then(r => r.json())
      .then(data => {
        if (data.success) {
          setVisibleKeys(data.visible || []);
          setIsAdmin(data.isAdmin || false);
        }
      })
      .catch(() => {});
  };

  // 根据权限过滤菜单
  const alwaysVisible = new Set(['home']);
  const currentUser = localStorage.getItem('notes_user') || '';
  const visibleMainItems = MAIN_MENU_ITEMS.filter(item => {
    if (alwaysVisible.has(item.key)) return true;
    return visibleKeys.includes(item.key);
  });
  const visibleAdminItems = ADMIN_MENU_ITEMS.filter(item => {
    if (!item.adminOnly) return true;
    return isAdmin;
  });

  const isActive = (path) => {
    if (path === '/') return location.pathname === '/' || location.pathname === '/home' || location.pathname === '/portal';
    return location.pathname.startsWith(path);
  };

  const handleNav = (path) => {
    navigate(path);
    setSidebarOpen(false);
  };

  const handleLogout = () => {
    setUserMenuOpen(false);
    localStorage.removeItem('notes_token');
    localStorage.removeItem('notes_user');
    localStorage.removeItem('notes_realname');
    if (onLogout) onLogout();
    else navigate('/');
  };

  const handleChangePassword = () => {
    setUserMenuOpen(false);
    setShowPasswordModal(true);
  };

  const handlePasswordChange = async (oldPwd, newPwd) => {
    try {
      const token = localStorage.getItem('notes_token');
      const res = await fetch('/api/auth/change-password', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'x-token': token },
        credentials: 'include',
        body: JSON.stringify({ oldPassword: oldPwd, newPassword: newPwd })
      });
      if (res.ok) {
        alert('密码修改成功，请重新登录');
        handleLogout();
      } else {
        const data = await res.json();
        alert(data.error || '密码修改失败');
      }
    } catch (e) {
      console.error('修改密码失败:', e);
      alert('修改密码失败');
    }
  };

  // 当前页面标题
  const getPageTitle = () => {
    const allItems = [...MAIN_MENU_ITEMS, ...ADMIN_MENU_ITEMS];
    const item = allItems.find(m => m.path !== '/' && location.pathname.startsWith(m.path));
    return item?.label || '首页';
  };

  return (
    <>
      <div className="top-navbar">
        <div className="navbar-left">
          <button className="hamburger-btn" onClick={() => setSidebarOpen(!sidebarOpen)}>
            ☰
          </button>
          <div className="navbar-title">知识管理系统</div>
        </div>
        <div className="navbar-right">
          <NotificationBell token={localStorage.getItem('notes_token')} compact />
          <div className="user-menu-wrapper">
            <span className="user-name" onClick={() => setUserMenuOpen(!userMenuOpen)}>{username}</span>
            {userMenuOpen && (
              <div className="user-dropdown">
                <div className="dropdown-item" onClick={handleChangePassword}>修改密码</div>
                <div className="dropdown-item" onClick={handleLogout}>退出登录</div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* 左侧抽屉导航 */}
      {sidebarOpen && (
        <>
          <div className="sidebar-overlay visible" onClick={() => setSidebarOpen(false)}></div>
          <div className="sidebar-drawer">
            <div className="sidebar-drawer-header">
              <h3>导航菜单</h3>
              <button className="close-btn" onClick={() => setSidebarOpen(false)}>✕</button>
            </div>
            <div className="sidebar-drawer-menu">
              {visibleMainItems.map(item => (
                <div
                  key={item.key}
                  className={`drawer-menu-item ${isActive(item.path) ? 'active' : ''}`}
                  onClick={() => handleNav(item.path)}
                >
                  {item.icon} {item.label}
                </div>
              ))}
              {visibleAdminItems.length > 0 && (
                <>
                  <div className="drawer-menu-divider">管理菜单</div>
                  {visibleAdminItems.map(item => (
                    <div
                      key={item.key}
                      className={`drawer-menu-item ${isActive(item.path) ? 'active' : ''}`}
                      onClick={() => handleNav(item.path)}
                    >
                      {item.icon} {item.label}
                    </div>
                  ))}
                </>
              )}
            </div>
          </div>
        </>
      )}

      {/* 修改密码弹窗 */}
      {showPasswordModal && (
        <div className="quality-modal-overlay" onClick={() => setShowPasswordModal(false)}>
          <div className="password-modal" onClick={e => e.stopPropagation()}>
            <div className="quality-modal-header">
              <h3>修改密码</h3>
              <button className="quality-close-btn" onClick={() => setShowPasswordModal(false)}>✕</button>
            </div>
            <div className="password-modal-body">
              <form onSubmit={async (e) => {
                e.preventDefault();
                const formData = new FormData(e.target);
                await handlePasswordChange(formData.get('oldPassword'), formData.get('newPassword'));
              }}>
                <div className="form-group">
                  <label>原密码</label>
                  <input type="password" name="oldPassword" required />
                </div>
                <div className="form-group">
                  <label>新密码</label>
                  <input type="password" name="newPassword" required minLength={6} />
                </div>
                <div className="form-group">
                  <label>确认新密码</label>
                  <input type="password" name="confirmPassword" required minLength={6} />
                </div>
                <div className="form-actions">
                  <button type="button" onClick={() => setShowPasswordModal(false)}>取消</button>
                  <button type="submit">确认修改</button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default TopNavbar;
